import type { EducatorEnrollmentWindow } from '@mc/api'
import { formatEnrollmentDate } from './formatEnrollmentDate'
import type { EducatorEnrollmentWindowType } from './resolveEnrollmentWindow'

export type HeroStripVariant = 'applications' | 'nextCohort' | 'legacy'

/**
 * A single hero-strip column. Copy is deferred to the consumer: the component
 * renders `t(labelKey)` and `value ?? t(valueFallbackKey)`. Label keys live in
 * the shared `enrollment.hero.*` namespace so all three surfaces render the
 * same copy from one source.
 */
export interface HeroStripColumn {
  labelKey: string
  /** Dynamic value (e.g. a formatted date); falls back to `valueFallbackKey`. */
  value?: string
  valueFallbackKey: string
}

export interface HeroStripConfig {
  variant: HeroStripVariant
  /** Present for `applications` / `nextCohort`; absent for `legacy`. */
  primaryColumn?: HeroStripColumn
  secondaryColumn?: HeroStripColumn
}

type EnrollmentWindowSchedule = Pick<
  EducatorEnrollmentWindow,
  'windowType' | 'startsAt' | 'endsAt'
>

const APPLICATIONS_DUE_LABEL_KEY = {
  early_enrollment: 'enrollment.hero.earlyApplicationsDueLabel',
  priority_enrollment: 'enrollment.hero.priorityApplicationsDueLabel',
  general_enrollment: 'enrollment.hero.applicationsDueLabel',
} as const

const findWindow = (
  windows: EnrollmentWindowSchedule[] | undefined,
  windowType: EducatorEnrollmentWindowType,
) => windows?.find((window) => window.windowType === windowType)

interface DeriveHeroStripArgs {
  /** The already-resolved window type (see `resolveEffectiveWindowType`). */
  windowType: EducatorEnrollmentWindowType | undefined
  /** The active window's `endsAt`; formatted into the Applications Due date. */
  currentWindowEndsAt?: string
  /** Every window on the active cohort; Start Date comes from `closed`. */
  enrollmentWindows?: EnrollmentWindowSchedule[]
  applicationDeadline?: string
  programStartDate?: string
  nextCohortName?: string
}

/**
 * Pure mapping from a resolved `windowType` to the hero strip's two dynamic
 * columns. No React, no `t()`.
 *
 *   early/priority/general → `applications` (Applications Due + Start Date)
 *   final_pay_by/closed    → `nextCohort`   (Next Cohort + Seat Availability)
 *   undefined              → `legacy`       (surface keeps its default strip)
 *
 * Applications Due is the active window's end, labelled by that window
 * (Early / Priority / plain), and Start Date is the `closed` window's start.
 */
export const deriveHeroStrip = ({
  windowType,
  currentWindowEndsAt,
  enrollmentWindows,
  applicationDeadline,
  programStartDate,
  nextCohortName,
}: DeriveHeroStripArgs): HeroStripConfig => {
  if (
    windowType === 'early_enrollment' ||
    windowType === 'priority_enrollment' ||
    windowType === 'general_enrollment'
  ) {
    const closed = findWindow(enrollmentWindows, 'closed')

    return {
      variant: 'applications',
      primaryColumn: {
        labelKey: APPLICATIONS_DUE_LABEL_KEY[windowType],
        value: formatEnrollmentDate(currentWindowEndsAt) ?? applicationDeadline,
        valueFallbackKey: 'enrollment.hero.applicationDeadlineFallback',
      },
      secondaryColumn: {
        labelKey: 'enrollment.hero.startDateLabel',
        value: formatEnrollmentDate(closed?.startsAt) ?? programStartDate,
        valueFallbackKey: 'enrollment.hero.programStartDateFallback',
      },
    }
  }

  if (windowType === 'final_pay_by' || windowType === 'closed') {
    return {
      variant: 'nextCohort',
      primaryColumn: {
        labelKey: 'enrollment.hero.nextCohortLabel',
        value: nextCohortName,
        valueFallbackKey: 'enrollment.hero.nextCohortNameFallback',
      },
      secondaryColumn: {
        labelKey: 'enrollment.hero.seatAvailabilityLabel',
        valueFallbackKey: 'enrollment.hero.seatAvailabilityValueLimited',
      },
    }
  }

  return { variant: 'legacy' }
}
